const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3000";

const request = async (path, opts = {}) => {
  const { headers: extraHeaders, body, ...rest } = opts;
  const res = await fetch(`${API_BASE}/api/admin${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(extraHeaders || {}) },
    ...rest,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let msg = res.statusText;
    let code = null;
    try {
      const j = await res.json();
      msg = j.message || j.error || msg;
      code = j.error || null;
    } catch {}
    const err = new Error(msg);
    err.status = res.status;
    err.code = code;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
};

export const adminApi = {
  get: (p, headers) => request(p, { headers }),
  post: (p, body, headers) => request(p, { method: "POST", body, headers }),
  put: (p, body, headers) => request(p, { method: "PUT", body, headers }),
  patch: (p, body, headers) => request(p, { method: "PATCH", body, headers }),
  del: (p, headers) => request(p, { method: "DELETE", headers }),
};

// Helper that pulls the items array out of a paginated or legacy response.
export const itemsOf = (resp) => {
  if (Array.isArray(resp)) return { items: resp, total: resp.length, hasMore: false, page: 1, limit: resp.length };
  if (resp && Array.isArray(resp.items)) return resp;
  return { items: [], total: 0, hasMore: false, page: 1, limit: 0 };
};

// ── Metrics ──────────────────────────────────────────────────
export const getOverview         = () => adminApi.get("/metrics/overview");
export const getTrialsEnding     = () => adminApi.get("/metrics/trials-ending");
export const getPlanDistribution = () => adminApi.get("/metrics/plan-distribution");
export const getRevenueSeries    = (range = 30) => adminApi.get(`/metrics/revenue?range=${range}`);
export const getSignupSeries     = (range = 30) => adminApi.get(`/metrics/signups?range=${range}`);
export const getActivitySeries   = (range = 24) => adminApi.get(`/metrics/activity?range=${range}`);

// ── Companies ────────────────────────────────────────────────
export const listCompanies      = (q = "") => adminApi.get(`/companies${q}`);
export const getCompany         = (id) => adminApi.get(`/companies/${id}`);
export const updateCompany      = (id, body) => adminApi.patch(`/companies/${id}`, body);
export const suspendCompany     = (id, reason) => adminApi.post(`/companies/${id}/suspend`, { reason });
export const unsuspendCompany   = (id) => adminApi.post(`/companies/${id}/unsuspend`, {});
export const extendTrial        = (id, days, reason) => adminApi.post(`/companies/${id}/extend-trial`, { days, reason });
export const assignPlanToCompany = (id, plan, months) => adminApi.post(`/companies/${id}/assign-plan`, { plan, months });
export const deleteCompany      = (id) => adminApi.del(`/companies/${id}`);
export const hardDeleteCompany  = (id, password) => adminApi.post(`/companies/${id}/hard-delete`, { password });

// ── Subscriptions ────────────────────────────────────────────
export const listSubscriptions  = (q = "") => adminApi.get(`/subscriptions${q}`);
export const getSubscription    = (id) => adminApi.get(`/subscriptions/${id}`);
export const changePlan         = (id, plan, months) => adminApi.post(`/subscriptions/${id}/change-plan`, { plan, months });
export const cancelSubscription = (id, mode) => adminApi.post(`/subscriptions/${id}/cancel`, { mode });
export const reactivateSub      = (id) => adminApi.post(`/subscriptions/${id}/reactivate`, {});
export const grantFreeAccess    = (id, months) => adminApi.post(`/subscriptions/${id}/grant-free-access`, { months });
export const runSubscriptionSweep = (asOf) => adminApi.post(`/subscriptions/run-sweep`, asOf ? { asOf } : {});

// ── Invoices ─────────────────────────────────────────────────
export const listInvoices       = (q = "") => adminApi.get(`/invoices${q}`);
export const getInvoice         = (id) => adminApi.get(`/invoices/${id}`);
export const markInvoicePaid    = (id) => adminApi.post(`/invoices/${id}/mark-paid`, {});
export const voidInvoice        = (id) => adminApi.post(`/invoices/${id}/void`, {});
export const refundInvoice      = (id, amount, reason, idempotencyKey) =>
  adminApi.post(`/invoices/${id}/refund`, { amount, reason }, idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined);
export const createManualInvoice = (body) => adminApi.post(`/invoices/manual`, body);

// ── Plans ────────────────────────────────────────────────────
export const listPlans          = () => adminApi.get("/plans");
export const getPlan            = (key) => adminApi.get(`/plans/${key}`);
export const updatePlan         = (key, body) => adminApi.patch(`/plans/${key}`, body);

// ── System ───────────────────────────────────────────────────
export const getSystemHealth    = () => adminApi.get("/system/health");
export const listWebhooks       = (q = "") => adminApi.get(`/system/webhooks${q}`);
export const getWebhook         = (id) => adminApi.get(`/system/webhooks/${id}`);
export const retryWebhook       = (id) => adminApi.post(`/system/webhooks/${id}/retry`, {});

// ── Audit log ────────────────────────────────────────────────
export const listAuditLog       = (q = "") => adminApi.get(`/audit-log${q}`);
export const listAuditActors    = () => adminApi.get(`/audit-log/actors`);

// ── Admins ───────────────────────────────────────────────────
export const listAdmins         = () => adminApi.get("/admins");
export const inviteAdmin        = (body) => adminApi.post("/admins", body);
export const revokeAdmin        = (id) => adminApi.del(`/admins/${id}`);

// ── Settings (maintenance mode, engagement jobs, platform flags) ──
export const getPlatformSettings = () => adminApi.get("/settings");
export const setMaintenanceMode  = (enabled, message) => adminApi.put("/settings/maintenance", { enabled, message });
export const setInactivityConfig = (enabled, days) => adminApi.put("/settings/inactivity", { enabled, days });
export const setUsageReportConfig = (enabled, dayOfWeek) => adminApi.put("/settings/usage-report", { enabled, dayOfWeek });
export const runInactivityNow    = () => adminApi.post("/settings/inactivity/run", {});
export const runUsageReportNow   = () => adminApi.post("/settings/usage-report/run", {});

// ── Coupons ──────────────────────────────────────────────────
export const listCoupons        = () => adminApi.get("/coupons");
export const createCoupon        = (body) => adminApi.post("/coupons", body);
export const updateCoupon        = (id, body) => adminApi.patch(`/coupons/${id}`, body);
export const deleteCoupon        = (id) => adminApi.del(`/coupons/${id}`);

// ── IP blocklist ─────────────────────────────────────────────
export const listBlockedIps     = () => adminApi.get("/ip-blocks");
export const blockIp            = (ip, reason) => adminApi.post("/ip-blocks", { ip, reason });
export const unblockIp          = (id) => adminApi.del(`/ip-blocks/${id}`);

// ── Impersonation ────────────────────────────────────────────
export const impersonateCompany = (companyId) => adminApi.post(`/impersonate/company/${companyId}`, {});
export const impersonateUser    = (userId) => adminApi.post(`/impersonate/user/${userId}`, {});

// ── Changelog / Latest Updates ───────────────────────────────
export const listChangelog      = () => adminApi.get("/changelog");
export const createChangelog     = (body) => adminApi.post("/changelog", body);
export const updateChangelog     = (id, body) => adminApi.patch(`/changelog/${id}`, body);
export const deleteChangelog     = (id) => adminApi.del(`/changelog/${id}`);

// ── Support ──────────────────────────────────────────────────
export const listSupportTickets = (q = "") => adminApi.get(`/support${q}`);
export const getSupportTicket   = (id) => adminApi.get(`/support/${id}`);
export const updateSupportTicket = (id, body) => adminApi.patch(`/support/${id}`, body);
export const getSupportTicketCount = () => adminApi.get("/support/count");
export const listSupportMessages = (id) => adminApi.get(`/support/${id}/messages`);
export const postSupportMessage  = (id, body) => adminApi.post(`/support/${id}/messages`, { body });
